import type { CSS } from '../../stitches.config';

/** Which edges get the fade. Match roughly to the scroll axis when possible. */
export type FadeEdge =
  | 'all'
  | 'vertical'
  | 'horizontal'
  | 'top'
  | 'bottom'
  | 'start'
  | 'end'
  | 'none';

/** Default space token used for fade size when not specified (e.g. from gutter). */
const FADE_MASK_SIZE_FALLBACK = '$6';

/** Multiplier applied to the fade size (e.g. 1.5 = 50% larger than the given token). */
const FADE_MASK_SIZE_MULTIPLIER = 1.5;

/**
 * Resolves a space token (e.g. $6, $16) to a CSS length 50% larger than the
 * padding, rounded up to the nearest whole value, using the design system's
 * space scale (var(--space-N)) so sizing stays scalable.
 *
 * Exported so a scroll container can pin `scroll-padding` to the same length: without it
 * `scrollIntoView` parks a keyboard-focused child flush against the scrollport, under the fade.
 */
export function fadeMaskSizeCSS(token: string): string {
  const keyStr = token.startsWith('$') ? token.slice(1) : token;
  const px = Number(keyStr);
  if (!Number.isFinite(px) || px < 0) {
    return `var(--space-${keyStr})`;
  }
  const rounded = Math.ceil(px * FADE_MASK_SIZE_MULTIPLIER);
  return `var(--space-${rounded})`;
}

function maskTop(effectiveSize: string): string {
  return `linear-gradient(to bottom, transparent, black ${effectiveSize})`;
}

function maskBottom(effectiveSize: string): string {
  return `linear-gradient(to bottom, black calc(100% - ${effectiveSize}), transparent)`;
}

function maskStart(effectiveSize: string): string {
  return `linear-gradient(to right, transparent, black ${effectiveSize})`;
}

function maskEnd(effectiveSize: string): string {
  return `linear-gradient(to right, black calc(100% - ${effectiveSize}), transparent)`;
}

function maskVertical(effectiveSize: string): string {
  return `${maskTop(effectiveSize)}, ${maskBottom(effectiveSize)}`;
}

function maskHorizontal(effectiveSize: string): string {
  return `${maskStart(effectiveSize)}, ${maskEnd(effectiveSize)}`;
}

export function fadeMaskCSS(
  size: string = FADE_MASK_SIZE_FALLBACK,
  edges: FadeEdge = 'vertical',
): CSS {
  const effectiveSize = fadeMaskSizeCSS(size);
  switch (edges) {
    case 'all':
      return {
        maskImage: `${maskVertical(effectiveSize)}, ${maskHorizontal(effectiveSize)}`,
        maskComposite: 'intersect',
        WebkitMaskComposite: 'source-in',
      };
    case 'vertical':
      return {
        maskImage: maskVertical(effectiveSize),
        maskComposite: 'intersect',
        WebkitMaskComposite: 'source-in',
      };
    case 'horizontal':
      return {
        maskImage: maskHorizontal(effectiveSize),
        maskComposite: 'intersect',
        WebkitMaskComposite: 'source-in',
      };
    case 'top':
      return { maskImage: maskTop(effectiveSize) };
    case 'bottom':
      return { maskImage: maskBottom(effectiveSize) };
    case 'start':
      return { maskImage: maskStart(effectiveSize) };
    case 'end':
      return { maskImage: maskEnd(effectiveSize) };
    case 'none':
      return { maskImage: 'none' };
    default: {
      const exhaustiveCheck: never = edges;
      throw new Error(`Unexpected fade edge: ${String(exhaustiveCheck)}`);
    }
  }
}
